import clsx from 'clsx';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import type { MetricHistoryEntry } from '@arcanum-hq/shared-types';
import {
  useSignalFieldResolver,
  type SignalFieldResolverContext,
} from '@/hooks/use-signal-field-resolver';
import { excelSerialToDate } from '@/lib/format';
import { DealHeaderStrip } from '../DealHeaderStrip';
import { AssumptionIntegrity } from './AssumptionIntegrity';
import { BeforeAfterComparison } from './BeforeAfterComparison';
import { CapitalStackVisual } from './CapitalStackVisual';
import { PropertyVisualContext } from './PropertyVisualContext';
import { ValueAddStory } from './ValueAddStory';

// deal-signal-overhaul Phase 4 Session 5 (2026-05-12) + Lovable-parity rewrite
// (2026-05-12) — operating-asset variant of the Signal tab.
//
// builder-ux-overhaul Phase 3 Session 2 (2026-05-22): the inline
// `signal-title-strip` header block has been replaced by <DealHeaderStrip
// showHeroReturns>. The legacy `signal-title-strip` testid is preserved on an
// outer <div> wrapper so existing test selectors continue to resolve.
// `deal-header-strip` is now the canonical testid on the inner <header>.
// Session 5 migrates any selectors that need to switch.
//
// Layout mirrors `dashboard-arcanum/src/pages/BuilderPage.tsx`'s `SignalView`
// + `DealHeader` (lines 532-620, 790-823): a single dense terminal-style
// stack of hairline-divided zones, with the deal title + 5-cell hero strip
// merged into one row. Compact 8px-vertical padding per zone; 32px outer
// horizontal rhythm; sharp panel borders + 1px hairline dividers between
// zones.

export interface SignalOperatingAssetProps {
  /**
   * Auth (conversation) OR public (deal share) resolver context. Drives the
   * schema-driven mappings lookup used by every zone.
   */
  context: SignalFieldResolverContext;
  /** Model id — feeds the per-zone `useModel` cache key (for thresholds + format). */
  modelId: string;
  /** Model version — threaded through `<HeroReturns>` for parity with the route call sites. */
  modelVersion?: string;
  /** Aerial lat/lng from `agent_state.aerialLatLng` (auth) or `publicDeal.aerialLatLng` (public). */
  aerialLatLng?: { lat: number; lng: number } | null;
  /** Per-named-range history for sparkline rendering. */
  metricHistory?: Record<string, MetricHistoryEntry[]> | null;
  /** 4 for auth Builder Signal; 8 for public Deal Link. */
  pageSize: 4 | 8;
  /** Deal name shown in the title strip. */
  conversationTitle?: string;
  /** builder-ux-overhaul Phase 4 Session 2 — threaded to inner <DealHeaderStrip pillSlot>. */
  pillSlot?: ReactNode;
  className?: string;
}

function formatSublineScalar(
  raw: unknown,
  kind: 'integer' | 'date' | 'string'
): string | null {
  if (raw == null || raw === '') return null;
  if (kind === 'integer' && typeof raw === 'number' && Number.isFinite(raw)) {
    return raw.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  if (kind === 'date' && typeof raw === 'number' && Number.isFinite(raw)) {
    return excelSerialToDate(raw);
  }
  if (typeof raw === 'string') return raw;
  return String(raw);
}

export function SignalOperatingAsset({
  context,
  modelId,
  modelVersion,
  aerialLatLng,
  metricHistory,
  pageSize,
  conversationTitle,
  pillSlot,
  className,
}: SignalOperatingAssetProps) {
  const resolver = useSignalFieldResolver(context);

  // Subline matches Lovable's "175 UNITS · RESTON, VA · CLASS B+ MIDRISE · VALUE-ADD"
  // pattern — uppercase tokens joined with ` · `. Falls back to a single
  // "OPERATING ASSET" descriptor while loading.
  const subline = useMemo(() => {
    if (!resolver.isReady) return 'OPERATING ASSET';
    const units = formatSublineScalar(
      resolver.resolve('snapshotUnits'),
      'integer'
    );
    const market = formatSublineScalar(
      resolver.resolve('snapshotAddress'),
      'string'
    );
    const acquired = formatSublineScalar(
      resolver.resolve('snapshotAcquisitionDate'),
      'date'
    );
    const parts: string[] = [];
    if (units != null) parts.push(`${units} UNITS`);
    if (market != null) parts.push(market.toUpperCase());
    if (acquired != null) parts.push(`ACQUIRED ${acquired.toUpperCase()}`);
    parts.push('OPERATING ASSET');
    return parts.join(' · ');
  }, [resolver]);

  return (
    <div
      data-testid="signal-operating-asset"
      data-variant="operating-asset"
      className={clsx('flex-1 flex flex-col overflow-y-auto bg-bg', className)}
    >
      {/* Zone 1 + 2 merged — Title strip with 5-cell hero on the right.
          builder-ux-overhaul Phase 3 Session 2 (2026-05-22): the outer <div>
          preserves the legacy `signal-title-strip` testid so existing tests
          continue to resolve. `deal-header-strip` is the canonical testid on
          the inner <header> emitted by <DealHeaderStrip>. Session 5 migrates
          any selectors that need to switch to the canonical testid.
          Mirrors Lovable BuilderPage.tsx:537-619 (DealHeader). */}
      <div data-testid="signal-title-strip">
        <DealHeaderStrip
          conversationTitle={conversationTitle}
          signalVariant="operating-asset"
          subline={subline}
          showHeroReturns
          heroContext={context}
          heroModelId={modelId}
          heroModelVersion={modelVersion}
          heroMetricHistory={metricHistory}
          pillSlot={pillSlot}
        />
      </div>

      {/* Zone 3 — Property Visual Context (compact 170px 3-column slab) */}
      <section
        id="signal-section-property"
        data-testid="signal-property-row"
        aria-label="Property visual context"
        className="border-b border-panel-border flex-shrink-0"
      >
        <PropertyVisualContext
          context={context}
          variant="operating-asset"
          propertyLat={aerialLatLng?.lat ?? null}
          propertyLng={aerialLatLng?.lng ?? null}
        />
      </section>

      {/* Zone 4 — Value-Add Story (3 phase cards, dense, with arrows + metrics) */}
      <section
        id="signal-section-story"
        data-testid="signal-value-add-row"
        aria-label="Value-add story"
        className="px-8 py-2 border-b border-panel-border flex-shrink-0"
      >
        <ValueAddStory context={context} />
      </section>

      {/* Zone 5 + 6 merged — Before/After Comparison left, Capital Stack right */}
      <section
        data-testid="signal-comparison-capital-row"
        aria-label="In-place vs stabilized comparison + capital stack"
        className="border-b border-panel-border flex flex-shrink-0"
      >
        <div
          id="signal-section-comparison"
          data-testid="signal-before-after-col"
          className="flex-[3] min-w-0 border-r border-panel-border"
        >
          <BeforeAfterComparison context={context} />
        </div>
        <div
          data-testid="signal-capital-stack-col"
          className="flex-[2] min-w-0"
        >
          <CapitalStackVisual context={context} variant="operating-asset" />
        </div>
      </section>

      {/* Zone 7 — Assumption Integrity (paginated grid) */}
      <section
        id="signal-section-assumptions"
        data-testid="signal-assumptions-row"
        aria-label="Assumption integrity"
        className="px-8 py-2 flex-shrink-0"
      >
        <AssumptionIntegrity context={context} pageSize={pageSize} />
      </section>
    </div>
  );
}
